
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { AgendamentosList } from "@/components/AgendamentosList";
import { StatsCards } from "@/components/StatsCards";
import { ChartsSection } from "@/components/ChartsSection";
import { PainelAgendamentos } from "@/components/PainelAgendamentos";
import { QuadroEfetivados } from "@/components/QuadroEfetivados";
import { HorariosEspeciais } from "@/components/HorariosEspeciais";
import { Button } from "@/components/ui/button";
import { Store, Home } from "lucide-react";

export const Dashboard = () => {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Aplicar tema escuro automaticamente
    document.documentElement.classList.add('dark');

    // Verificar autenticação
    async function checkAuth() {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate("/auth");
        return;
      }

      // Verificar se o email está autorizado na info_loja
      const { data: lojaInfo } = await supabase
        .from("info_loja")
        .select("auth_user")
        .limit(1)
        .maybeSingle();

      if (!lojaInfo || lojaInfo.auth_user !== session.user.email) {
        await supabase.auth.signOut();
        navigate("/auth");
        return;
      }

      setIsAuthorized(true);
      setIsLoading(false);
    }

    checkAuth();
  }, [navigate]);

  if (isLoading || !isAuthorized) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Verificando autenticação...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-6 py-8">
        {/* Cabeçalho */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-primary mb-2">
            DASHBOARD DE AGENDAMENTOS
          </h1>
          <p className="text-muted-foreground text-lg">
            Análise completa e interativa dos seus agendamentos em tempo real
          </p>
        </div>

        {/* Botões de Ação - Estilo Skeuomorphic Black Glass */}
        <div className="mb-12 flex flex-col sm:flex-row justify-center items-center gap-6">
          {/* Botão Home */}
          <Button
            onClick={() => navigate('/')}
            size="lg"
            className="group relative px-6 py-5 sm:px-12 sm:py-8 h-auto bg-zinc-900 border border-white/10 rounded-2xl shadow-[0_8px_0_rgb(0,0,0),0_15px_30px_rgba(0,0,0,0.6)] hover:shadow-[0_4px_0_rgb(0,0,0),0_10px_20px_rgba(0,0,0,0.5)] active:shadow-none active:translate-y-[8px] transition-all overflow-hidden w-full sm:w-auto"
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="flex items-center gap-4 relative z-10">
              <div className="p-2 sm:p-3 bg-zinc-800 rounded-xl border border-white/5 shadow-inner">
                <Home className="h-8 w-8 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
              </div>
              <span className="text-xl sm:text-2xl font-black text-white tracking-normal sm:tracking-widest drop-shadow-[0_0_10px_rgba(255,255,255,0.8)] uppercase">
                HOME
              </span>
            </div>
          </Button>

          {/* Botão Cadastrar Produtos */}
          <Button
            onClick={() => navigate('/loja')}
            size="lg"
            className="group relative px-6 py-5 sm:px-12 sm:py-8 h-auto bg-zinc-900 border border-white/10 rounded-2xl shadow-[0_8px_0_rgb(0,0,0),0_15px_30px_rgba(0,0,0,0.6)] hover:shadow-[0_4px_0_rgb(0,0,0),0_10px_20px_rgba(0,0,0,0.5)] active:shadow-none active:translate-y-[8px] transition-all overflow-hidden w-full sm:w-auto"
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="flex items-center gap-4 relative z-10">
              <div className="p-2 sm:p-3 bg-zinc-800 rounded-xl border border-white/5 shadow-inner">
                <Store className="h-8 w-8 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
              </div>
              <span className="text-xl sm:text-2xl font-black text-white tracking-normal sm:tracking-widest drop-shadow-[0_0_10px_rgba(255,255,255,0.8)] uppercase">
                CADASTRAR PRODUTOS
              </span>
            </div>
          </Button>
        </div>

        {/* Cards de Estatísticas */}
        <StatsCards />

        {/* Quadro de Efetivados do Dia */}
        <div className="mb-8">
          <div className="grid gap-6 md:grid-cols-4">
            <QuadroEfetivados />
          </div>
        </div>

        {/* Horários Especiais */}
        <div className="mb-8">
          <HorariosEspeciais />
        </div>

        {/* Lista de Agendamentos Segmentados */}
        <div className="mb-8">
          <AgendamentosList />
        </div>

        {/* Painel de Agendamentos em Tempo Real */}
        <div className="mb-8">
          <PainelAgendamentos />
        </div>

        {/* Gráficos */}
        <ChartsSection />
      </main>
    </div>
  );
};
